import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  IAllTransactionResponseDTO,
  ITransactionReportConfig,
  ITransactionFilter,
} from '../../models/transaction-report.models';

import { environment } from '../../../../../../environments/environment';
import { ALL_TRANSACTION_REPORT_CONFIG } from '../../config/transaction-report.config';
import TransactionService from '../../services/transaction.service';

import {
  ColDef,
  GridApi,
  GridReadyEvent,
  PaginationChangedEvent,
  ColumnApi,
  Column,
  SortChangedEvent,
  FilterChangedEvent,
  FilterModifiedEvent,
  FilterOpenedEvent,
  RowClickedEvent
} from 'ag-grid-community';

const { defaultPageSizeArr } = environment;

@Component({
  selector: 'app-transaction-report',
  templateUrl: './transaction-report.component.html',
  styleUrls: ['./transaction-report.component.scss'],
})
export class TransactionReportComponent implements OnInit, OnDestroy {
  public config: ITransactionReportConfig = ALL_TRANSACTION_REPORT_CONFIG;
  public pageSize: number = 10;
  public currentPage: number = 1;
  public collectionSize: number = 0;
  public loading: boolean = false;
  public pageSizeArr: Array<number> = defaultPageSizeArr;
  public globalSearch: string = '';
  public activeFilters: string[] = [];

  // Sorting and pagination parameters
  public page_no: number = 0;
  public page_size: number = 10;
  public sort_dir: string = 'ASC';
  public sort_column: string = '';

  public rowData: IAllTransactionResponseDTO[] = [];
  public columnDefs: ColDef[] = this.config.columnDefs ;

  columnDefsCopy: ColDef[] = [];
  public tableSelectedColumn: ColDef[] = [];

  public defaultColDef: ColDef = {
    flex: 1,
    minWidth: 180,
    resizable: true,
    sortable: true,
    filter: false,
    autoHeight: true,
  };

  private gridApi!: GridApi<IAllTransactionResponseDTO>;
  private gridColumnApi!: ColumnApi;

  Math = Math;

  constructor(private transactionService: TransactionService) {
    // Initialize column selections
    if (this.columnDefs && this.columnDefs.length > 0) {
      this.columnDefsCopy = this.columnDefs.slice();
      this.tableSelectedColumn = this.columnDefs.slice();
    }
  }

  ngOnInit(): void {
    // Ensure column definitions are initialized
    if (!this.tableSelectedColumn || this.tableSelectedColumn.length === 0) {
      this.columnDefsCopy = this.columnDefs.slice();
      this.tableSelectedColumn = this.columnDefs.slice();
    }
    this.transactionList();
  }

  transactionList() {
    this.loading = true;
    // Sync page_no with currentPage (convert to 0-based index)
    this.page_no = this.currentPage - 1;
    this.page_size = this.pageSize;

    const filter: ITransactionFilter = {
      page_no: this.page_no,
      page_size: this.page_size,
      sort_column: this.sort_column,
      sort_dir: this.sort_dir,
      filters: this.activeFilters
    };
    this.transactionService.getTransactionList(filter).subscribe({
      next: (response) => {
        const page = response?.response?.data;
        this.rowData = page?.content ?? [];
        this.collectionSize = page?.totalElements ?? this.rowData.length;
        this.loading = false;
      },
      error: () => {
        this.rowData = [];
        this.collectionSize = 0;
        this.loading = false;
      },
    });
  }

  onFilterSearch(filters: string[]): void {
    this.activeFilters = filters;
    this.currentPage = 1;
    this.page_no = 0;
    this.transactionList();
  }

  onFilterReset(): void {
    this.activeFilters = [];
    this.currentPage = 1;
    this.page_no = 0;
    this.transactionList();
  }

  onGridReady(params: GridReadyEvent<IAllTransactionResponseDTO>) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    console.log('Grid Ready');
    console.log('Columns:', this.columnDefs);

    const allColumnIds: (string | Column)[] = [];

    params.columnApi.getColumns()?.forEach((column) => {
      allColumnIds.push(column.getId());
    });

    console.log('Column IDs:', allColumnIds);

    params.columnApi.autoSizeColumns(allColumnIds, false);
  }

  onPaginationChange(pageNumber: number) {
    if (pageNumber !== this.currentPage) {
      this.currentPage = pageNumber;
      this.page_no = pageNumber - 1;
      this.transactionList();
    }
  }

  onSortChange(event: any) {
    console.log('Sort changed:', event);
    // Get sort model from grid column API
    const sortModel = this.gridColumnApi?.getColumnState().filter((col: any) => col.sort !== null);

    if (sortModel && sortModel.length > 0) {
      this.sort_column = sortModel[0].colId;
      this.sort_dir = sortModel[0].sort === 'asc' ? 'ASC' : 'DESC';
      console.log(`Sorted by: ${this.sort_column} ${this.sort_dir}`);

      // Reset to first page when sorting changes
      this.currentPage = 1;
      this.page_no = 0;
      this.transactionList();
    }
  }

  onPageSizeChanges($event: Event) {
    const value = Number(($event.target as HTMLSelectElement).value);
    if (!Number.isFinite(value) || value <= 0 || value === this.pageSize) {
      return;
    }

    this.pageSize = value;
    this.page_size = value;
    this.currentPage = 1;
    this.page_no = 0;
    this.gridApi?.paginationSetPageSize(value);
    this.transactionList();
  }

  onFilterChange(param: FilterChangedEvent | any) {
    console.log('Filter changed:', param);
  }

  onFilterModified(param: FilterModifiedEvent | any) {
    console.log('Filter modified:', param);
  }

  onFilterOpened(param: FilterOpenedEvent | any) {
    console.log('Filter opened:', param);
  }

  onRowClicked(param: RowClickedEvent | any) {
    console.log('Row clicked:', param);
  }

  async onRowDoubleClicked(param: RowClickedEvent | any) {
    console.log('Row double clicked:', param);
  }

  onExportCSV(): void {
    this.loading = true;
    const filter: ITransactionFilter = {
      page_no: this.page_no,
      page_size: this.page_size,
      sort_column: this.sort_column,
      sort_dir: this.sort_dir,
      filters: this.activeFilters
    };

    this.transactionService.exportTransactionCSV(filter).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transactions_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error exporting CSV:', error);
        this.loading = false;
      },
    });
  }

  updateSelectedColumn(event: any) {
    if (event.target.checked) {
      if (this.tableSelectedColumn.indexOf(this.columnDefsCopy[parseInt(event.target.value)]) < 0) {
        this.tableSelectedColumn.splice(parseInt(event.target.value), 0, this.columnDefsCopy[parseInt(event.target.value)]);
      }
    } else {
      if (this.tableSelectedColumn.indexOf(this.columnDefsCopy[parseInt(event.target.value)]) > -1) {
        this.tableSelectedColumn.splice(this.tableSelectedColumn.indexOf(this.columnDefsCopy[parseInt(event.target.value)]), 1);
      }
    }
    this.gridApi.setColumnDefs(this.tableSelectedColumn);
    this.gridApi.setDefaultColDef(this.defaultColDef);
  }

  ngOnDestroy(): void {}
}
